/**
 * Timeline Editor Component (canvas-v2)
 *
 * Main timeline component assembling ruler, playhead, and tracks.
 * Provides horizontal scrollable timeline view.
 *
 * V2 additions:
 * - Computes audioTrackCount from max trackIndex of audio clips
 * - Toolbar with type-aware Split/Delete/Copy/Paste/Undo/Redo buttons
 */

import { useRef, useCallback, useEffect, useState, useMemo } from 'react'
import { TimelineRuler } from './TimelineRuler'
import { TimelinePlayhead } from './TimelinePlayhead'
import { TimelineTrack } from './TimelineTrack'
import { TrackHeaderColumn, TRACK_HEADER_COLUMN_DEFAULT_WIDTH, TRACK_HEADER_COLUMN_MIN, TRACK_HEADER_COLUMN_MAX, type TrackCell } from './TrackHeaderColumn'
import type { TimelineState } from './types'
import { createNadDebug } from '@ponpon/api-client'

// RFC-0630 v3 (architect-review #4 + low item #7): observability for the
// trim-cursor click-suppress. When the user trims a clip and the trailing
// pointerup → click lands on the timeline area, we early-return to keep
// the playhead from jumping. That early-return now emits a structured log
// line so the path is visible in NAD traces.
const trimCursorLog = createNadDebug('dw:video-editor:trim-cursor')
// RFC-0631 round-3 (Issue 3): emit a throttled scroll log so NAD traces
// can confirm the fixed-left header column does NOT translate when the
// timeline body scrolls horizontally.
const layoutLog = createNadDebug('dw:video-editor:layout')

interface TimelineEditorProps {
  /** Timeline state (controlled by parent) */
  timelineState: TimelineState

  /** Replace selection with the given clip (non-shift click) */
  onSelectClip: (clipId: string | null, clipType?: 'video' | 'audio' | null) => void

  /** Toggle a clip's membership in the multi-selection (shift+click) */
  onToggleClipSelection?: (clipId: string) => void

  /** Clear the entire selection (e.g. clicking an empty area) */
  onClearSelection?: () => void

  /** Apply a uniform startOffset delta + optional track delta to ALL selected clips (multi-drag commit) */
  onMultiDragOffset?: (deltaSec: number, trackDelta?: number) => void

  /** Close-gaps callback (playhead-driven — closes every gap at or after the cursor on each track; does NOT delete clips). */
  onRippleDelete?: () => void

  /** Set playhead callback */
  onSetPlayhead: (position: number) => void

  /** Set zoom callback */
  onSetZoom: (zoomLevel: number) => void

  /** Reorder clip callback */
  onReorderClip: (clipId: string, newOrder: number) => void

  /** Video offset change callback */
  onClipOffsetChange?: (clipId: string, startOffset: number) => void

  /** Video track index change callback */
  onClipTrackIndexChange?: (clipId: string, newTrackIndex: number) => void

  /** Trim clip callback */
  onTrimClip: (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => void

  /** Live trim preview callback (no history push) */
  onTrimClipPreview?: (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => void

  /** Audio offset change callback */
  onAudioOffsetChange?: (clipId: string, startOffset: number) => void

  /** Audio trim callback */
  onAudioTrimClip?: (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => void

  /** Live audio trim preview callback (no history push) */
  onAudioTrimClipPreview?: (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => void

  /** Audio track index change callback */
  onAudioTrackIndexChange?: (clipId: string, newTrackIndex: number) => void

  /** Called on mouseUp after a trim-start commit so any negative startOffset is normalized (Issue 1) */
  onNormalizeTimelineOrigin?: () => void

  /** Ref tracking playhead position at 60fps during playback */
  playheadPositionRef?: React.MutableRefObject<number>

  /** Whether timeline is read-only */
  readOnly?: boolean

  /** Callback to split the selected clip at playhead */
  onSplitSelectedClip?: () => void

  /** Callback to delete the selected clip */
  onDeleteSelectedClip?: () => void

  /** Callback to copy the selected clip */
  onCopyClip?: () => void

  /** Callback to paste the copied clip */
  onPasteClip?: () => void

  /** Callback to undo */
  onUndo?: () => void

  /** Callback to redo */
  onRedo?: () => void

  /** Whether undo is available */
  canUndo?: boolean

  /** Whether redo is available */
  canRedo?: boolean

  /** Add a new track */
  onAddTrack?: (type: 'video' | 'audio') => void

  /**
   * RFC-0631 round-3 (Issue 5): Remove a sub-track. The `deleteClips`
   * argument is true only after the user accepted the confirm prompt
   * (when the sub-track was non-empty). The reducer in useTimelineState
   * still protects against removing track 0 (Main track) and against
   * dropping the track count below 1.
   */
  onRemoveTrack?: (
    trackKind: 'video' | 'audio',
    trackIndex: number,
    deleteClips: boolean,
  ) => void

  /** Drop a reusable left-panel reference on the timeline */
  onDropReference?: (
    clipId: string,
    clipType: 'video' | 'audio',
    startOffset: number,
    trackIndex: number
  ) => void

  /**
   * RFC-0651 #3: Main Track Magnet toggle. When provided, replaces the
   * one-shot "Close all gaps" toolbar button with a stateful toggle. The
   * actual pack-on-commit behavior is wired in TimelineEditorModal.
   */
  magnetEnabled?: boolean
  onSetMagnetEnabled?: (enabled: boolean) => void
  /**
   * RFC-0631 (Issue 3): Immediately pack track-0 gaps when the magnet toggle
   * flips ON. Without this, the magnet only takes effect on the next edit,
   * so the user has to "wiggle" a clip to see existing gaps close.
   */
  onMagnetPackTrack0?: () => void

  /** RFC-0651 #11: per-row track mute. */
  mutedVideoTracks?: Set<number>
  mutedAudioTracks?: Set<number>
  onToggleTrackMute?: (trackKind: 'video' | 'audio', trackIndex: number) => void
}

const RULER_HEIGHT = 30
const VIDEO_TRACK_HEIGHT = 100
const AUDIO_TRACK_HEIGHT = 60
const MIN_TIMELINE_WIDTH = 1200

export function TimelineEditor({
  timelineState,
  onSelectClip,
  onToggleClipSelection,
  onClearSelection,
  onMultiDragOffset,
  onRippleDelete: _onRippleDelete,
  onSetPlayhead,
  onSetZoom,
  onReorderClip,
  onClipOffsetChange,
  onClipTrackIndexChange,
  onTrimClip,
  onTrimClipPreview,
  onAudioOffsetChange,
  onAudioTrimClip,
  onAudioTrimClipPreview,
  onAudioTrackIndexChange,
  onNormalizeTimelineOrigin,
  playheadPositionRef,
  readOnly = false,
  onSplitSelectedClip,
  onDeleteSelectedClip,
  onCopyClip,
  onPasteClip,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  onAddTrack,
  onRemoveTrack,
  onDropReference,
  magnetEnabled = true,
  onSetMagnetEnabled,
  onMagnetPackTrack0,
  mutedVideoTracks,
  mutedAudioTracks,
  onToggleTrackMute,
}: TimelineEditorProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [snapPosition, setSnapPosition] = useState<number | null>(null)
  const [isTrackMenuOpen, setIsTrackMenuOpen] = useState(false)
  // RFC-0633 (Issue 3): user-resizable header column width. Persisted to
  // localStorage on pointerup; the splitter inside `TrackHeaderColumn` fires
  // live `onColumnWidthChange` on every pointermove for smooth dragging.
  const [headerColumnPx, setHeaderColumnPx] = useState<number>(() => {
    if (typeof window === 'undefined') return TRACK_HEADER_COLUMN_DEFAULT_WIDTH
    try {
      const raw = window.localStorage.getItem('dw:timeline:header-column-px')
      const parsed = raw === null ? NaN : Number(raw)
      if (Number.isFinite(parsed) && parsed >= TRACK_HEADER_COLUMN_MIN && parsed <= TRACK_HEADER_COLUMN_MAX) {
        return parsed
      }
    } catch {
      // ignore (e.g. cookies disabled)
    }
    return TRACK_HEADER_COLUMN_DEFAULT_WIDTH
  })
  const commitHeaderColumnPx = useCallback((next: number) => {
    setHeaderColumnPx(next)
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('dw:timeline:header-column-px', String(next))
      } catch {
        // ignore
      }
    }
  }, [])

  const audioTrackCount = useMemo(() => {
    const maxTrackIndex = timelineState.audioClips.length > 0
      ? Math.max(...timelineState.audioClips.map((c) => c.trackIndex))
      : 0
    return Math.max(timelineState.audioTrackCount, maxTrackIndex + 1)
  }, [timelineState.audioClips, timelineState.audioTrackCount])

  const videoTrackCount = useMemo(() => {
    const maxTrackIndex = timelineState.clips.length > 0
      ? Math.max(...timelineState.clips.map((c) => c.trackIndex))
      : 0
    return Math.max(timelineState.videoTrackCount, maxTrackIndex + 1)
  }, [timelineState.clips, timelineState.videoTrackCount])

  // Handle timeline click (move playhead). Clicking empty timeline area
  // also clears the multi-selection so it doesn't persist when the user
  // clicks "off" the clips.
  const handleTimelineClick = useCallback(
    (e: React.MouseEvent) => {
      if (readOnly) return

      // RFC-0651 #2: when a trim drag was just released, the trailing click
      // event must NOT move the playhead. TimelineClip sets this flag on
      // pointerdown of a trim handle and clears it on a setTimeout(0) after
      // pointerup so this click handler sees it.
      try {
        if (window.__dwTimelineTrimActive) {
          trimCursorLog({
            event: 'playhead-click-suppressed',
            reason: 'trim-active',
          })
          return
        }
      } catch {
        // ignore (e.g. cross-origin)
      }

      const container = scrollContainerRef.current
      if (!container) return

      const rect = container.getBoundingClientRect()
      const relativeX = e.clientX - rect.left + container.scrollLeft
      const time = relativeX / timelineState.zoomLevel

      const nextPosition = Math.max(0, Math.min(time, timelineState.totalDuration))
      if (playheadPositionRef) {
        playheadPositionRef.current = nextPosition
      }
      onSetPlayhead(nextPosition)
      // Empty-area click clears selection (clip-internal handlers stopPropagation)
      if (onClearSelection && !e.shiftKey) {
        onClearSelection()
      }
    },
    [readOnly, timelineState.zoomLevel, timelineState.totalDuration, onSetPlayhead, playheadPositionRef, onClearSelection]
  )

  // Handle zoom
  const handleZoom = useCallback(
    (delta: number) => {
      const newZoom = Math.max(10, Math.min(200, timelineState.zoomLevel + delta))
      onSetZoom(newZoom)
    },
    [timelineState.zoomLevel, onSetZoom]
  )

  // Scroll playhead into view if needed
  useEffect(() => {
    if (timelineState.isPlaying && scrollContainerRef.current) {
      const playheadPixelPos = timelineState.playheadPosition * timelineState.zoomLevel
      const container = scrollContainerRef.current
      const containerWidth = container.clientWidth
      const scrollLeft = container.scrollLeft

      // Auto-scroll if playhead is near edge
      if (playheadPixelPos > scrollLeft + containerWidth - 100) {
        container.scrollLeft = playheadPixelPos - containerWidth / 2
      }
    }
  }, [timelineState.playheadPosition, timelineState.zoomLevel, timelineState.isPlaying])

  const timelineWidth = Math.max(
    timelineState.totalDuration * timelineState.zoomLevel,
    MIN_TIMELINE_WIDTH
  )

  const playheadPixel = timelineState.playheadPosition * timelineState.zoomLevel

  const totalHeight = RULER_HEIGHT + VIDEO_TRACK_HEIGHT * videoTrackCount + AUDIO_TRACK_HEIGHT * audioTrackCount

  // RFC-0631 round-3 (Issues 3 + 5 + 6): build the parallel cells +
  // heights arrays for TrackHeaderColumn. Video sub-tracks first, then
  // audio sub-tracks. clipCount drives the confirm prompt for Remove.
  const headerCells = useMemo<TrackCell[]>(() => {
    const out: TrackCell[] = []
    for (let i = 0; i < videoTrackCount; i++) {
      out.push({
        kind: 'video',
        trackIndex: i,
        clipCount: timelineState.clips.filter((c) => c.trackIndex === i).length,
      })
    }
    for (let i = 0; i < audioTrackCount; i++) {
      out.push({
        kind: 'audio',
        trackIndex: i,
        clipCount: timelineState.audioClips.filter((c) => c.trackIndex === i).length,
      })
    }
    return out
  }, [
    videoTrackCount,
    audioTrackCount,
    timelineState.clips,
    timelineState.audioClips,
  ])

  const headerCellHeights = useMemo(() => {
    const out: number[] = []
    for (let i = 0; i < videoTrackCount; i++) out.push(VIDEO_TRACK_HEIGHT)
    for (let i = 0; i < audioTrackCount; i++) out.push(AUDIO_TRACK_HEIGHT)
    return out
  }, [videoTrackCount, audioTrackCount])

  // RFC-0631 round-3 (Issue 3): throttle scroll-x logs to once per 500ms
  // so traces stay readable while still confirming the column doesn't move.
  const lastScrollLogRef = useRef(0)
  const handleBodyScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget
    // RFC-0635 regression guard: after the overflow-y-clip fix the body must
    // not own a vertical scroll context. If scrollTop ever leaks > 0 here, the
    // promotion rule has crept back (e.g. someone reverted to overflow-y-visible
    // alongside overflow-x-auto) and the wheel sync is broken again.
    if (target.scrollTop !== 0) {
      layoutLog({ event: 'body-scroll-y-leaked', scrollTop: target.scrollTop })
    }
    const now = Date.now()
    if (now - lastScrollLogRef.current < 500) return
    lastScrollLogRef.current = now
    layoutLog({
      event: 'scroll-x-detected',
      scrollLeft: target.scrollLeft,
      scrollTop: target.scrollTop,
      headerColumnPx,
    })
  }, [headerColumnPx])

  // RFC-0634 (header-track scroll sync): the outer wrapper now owns the
  // shared vertical scroll context so the left header column and the right
  // track body translate together. Throttled to 500ms (same pattern as the
  // horizontal scroll-x-detected log) so NAD traces stay readable.
  const lastScrollYLogRef = useRef(0)
  const handleWrapperScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const now = Date.now()
    if (now - lastScrollYLogRef.current < 500) return
    lastScrollYLogRef.current = now
    const target = e.currentTarget
    layoutLog({
      event: 'scroll-y-sync',
      scrollTop: target.scrollTop,
      columnPx: headerColumnPx,
    })
  }, [headerColumnPx])

  // Type-aware toolbar helpers
  const selectionSize = timelineState.selectedClipIds.size
  const hasSelection = selectionSize > 0
  const isMultiSelected = selectionSize > 1
  const hasCopied = timelineState.copiedClip !== null

  // Derive single-selection projection for tooltips. We avoid pulling in the
  // helper module from a render path that's hot-looped — direct lookups
  // against a small Set are fine.
  const primarySelectedType: 'video' | 'audio' | 'mixed' | null = useMemo(() => {
    if (selectionSize === 0) return null
    let hasVideo = false
    let hasAudio = false
    for (const id of timelineState.selectedClipIds) {
      if (timelineState.clips.some((c) => c.id === id)) hasVideo = true
      if (timelineState.audioClips.some((c) => c.id === id)) hasAudio = true
    }
    if (hasVideo && hasAudio) return 'mixed'
    if (hasVideo) return 'video'
    if (hasAudio) return 'audio'
    return null
  }, [selectionSize, timelineState.selectedClipIds, timelineState.clips, timelineState.audioClips])

  return (
    <div className="flex flex-col h-full bg-card">
      {/* Toolbar: Zoom controls + editing actions */}
      <div className="flex items-center gap-2 px-4 py-2 bg-card-hover border-b border-border">
        {/* Zoom controls */}
        <span className="text-text-secondary text-sm">Zoom:</span>
        <input
          type="range"
          min="10"
          max="200"
          value={timelineState.zoomLevel}
          onChange={(e) => onSetZoom(Number(e.target.value))}
          className="w-24"
          disabled={readOnly}
        />
        <span
          className="text-text-secondary text-sm min-w-[3ch]"
          data-testid="timeline-zoom-display"
          data-zoom-level={timelineState.zoomLevel}
        >
          {Math.round((timelineState.zoomLevel / 50) * 100)}%
        </span>
        <button
          onClick={() => handleZoom(10)}
          className="px-2 py-1 bg-card text-foreground text-sm rounded hover:bg-canvas"
          disabled={readOnly}
        >
          +
        </button>
        <button
          onClick={() => handleZoom(-10)}
          className="px-2 py-1 bg-card text-foreground text-sm rounded hover:bg-canvas"
          disabled={readOnly}
        >
          -
        </button>

        {/* Separator */}
        <div className="w-px h-5 bg-border mx-1" />

        {/* Editing toolbar actions */}
        {!readOnly && (
          <>
            <ToolbarButton
              label="Split"
              icon={<SplitIcon />}
              onClick={onSplitSelectedClip}
              disabled={!hasSelection || isMultiSelected}
              title={
                isMultiSelected
                  ? 'Select exactly one clip to split'
                  : hasSelection
                    ? `Split ${primarySelectedType || ''} clip at playhead`
                    : 'Select a clip to split'
              }
            />
            <ToolbarButton
              label="Delete"
              icon={<DeleteIcon />}
              onClick={onDeleteSelectedClip}
              disabled={!hasSelection}
              title={hasSelection
                ? `Delete ${selectionSize} selected clip(s)`
                : 'Select a clip to delete'
              }
            />
            <ToolbarToggle
              label="Main Track Magnet"
              icon={<CloseAllGapsIcon />}
              pressed={magnetEnabled}
              onClick={() => {
                // RFC-0631 (Issue 3): When flipping ON, pack track-0 gaps
                // immediately so the user sees instant feedback rather than
                // having to make a follow-up edit.
                const next = !magnetEnabled
                onSetMagnetEnabled?.(next)
                if (next) onMagnetPackTrack0?.()
              }}
              disabled={!onSetMagnetEnabled}
              title={
                magnetEnabled
                  ? 'Main Track Magnet ON — releasing a trim/move/split on track 0 re-packs clips left-to-right. Click to disable.'
                  : 'Main Track Magnet OFF — clips on track 0 stay where you put them. Click to enable auto-packing.'
              }
              testid="timeline-magnet-toggle"
            />
            <ToolbarButton
              label="Copy"
              icon={<CopyIcon />}
              onClick={onCopyClip}
              disabled={!hasSelection || isMultiSelected}
              title={
                isMultiSelected
                  ? 'Multi-clip copy not supported (select one clip)'
                  : 'Copy selected clip'
              }
            />
            <ToolbarButton
              label="Paste"
              icon={<PasteIcon />}
              onClick={onPasteClip}
              disabled={!hasCopied}
              title={hasCopied
                ? `Paste ${timelineState.copiedClipType || ''} clip`
                : 'Copy a clip first'
              }
            />

            {/* Separator */}
            <div className="w-px h-5 bg-border mx-1" />

            <ToolbarButton
              label="Undo"
              icon={<UndoIcon />}
              onClick={onUndo}
              disabled={!canUndo}
              title="Undo (Ctrl+Z)"
            />
            <ToolbarButton
              label="Redo"
              icon={<RedoIcon />}
              onClick={onRedo}
              disabled={!canRedo}
              title="Redo (Ctrl+Shift+Z)"
            />
          </>
        )}

        {/* Playhead time display */}
        <div className="ml-auto text-foreground text-sm font-mono" data-testid="timeline-time-display">
          {formatTime(timelineState.playheadPosition)} / {formatTime(timelineState.totalDuration)}
        </div>
      </div>

      {/* Scrollable timeline area — split into [fixed header | scrolling body]
          (RFC-0631 round-3 Issue 3) + shared vertical scroll context
          (RFC-0634: header-track scroll sync). The header column stays put
          on horizontal scroll; the body owns its own overflow-x. Both
          share the same vertical scroll because the outer wrapper now
          owns overflow-y. */}
      <div
        className="flex-1 flex overflow-y-auto overflow-x-hidden"
        onScroll={handleWrapperScroll}
        data-testid="timeline-scroll-wrapper"
      >
        <TrackHeaderColumn
          cells={headerCells}
          cellHeights={headerCellHeights}
          rulerHeight={RULER_HEIGHT}
          mutedVideoTracks={mutedVideoTracks}
          mutedAudioTracks={mutedAudioTracks}
          onToggleTrackMute={onToggleTrackMute}
          onRemoveTrack={onRemoveTrack}
          locked={readOnly}
          columnPx={headerColumnPx}
          onColumnWidthChange={setHeaderColumnPx}
          onColumnWidthCommit={commitHeaderColumnPx}
        />
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-x-auto overflow-y-clip"
          onScroll={handleBodyScroll}
          data-testid="timeline-scroll-body"
        >
          <div
            className="relative"
            style={{ width: `${timelineWidth}px`, height: `${totalHeight}px` }}
            onClick={handleTimelineClick}
          >
            {/* Ruler */}
            <div className="sticky top-0 z-20">
              <TimelineRuler
                totalDuration={timelineState.totalDuration}
                zoomLevel={timelineState.zoomLevel}
                viewportWidth={MIN_TIMELINE_WIDTH}
                height={RULER_HEIGHT}
              />
            </div>

            {/* Video track */}
            <TimelineTrack
              type="video"
              label="Video"
              clips={timelineState.clips}
              zoomLevel={timelineState.zoomLevel}
              totalDuration={timelineState.totalDuration}
              selectedClipIds={timelineState.selectedClipIds}
              onSelectClip={(id) => onSelectClip(id, 'video')}
              onToggleClipSelection={onToggleClipSelection}
              onMultiDragOffset={onMultiDragOffset}
              onReorderClip={onReorderClip}
              onClipOffsetChange={onClipOffsetChange}
              onClipTrackIndexChange={onClipTrackIndexChange}
              onTrimClip={onTrimClip}
              onTrimClipPreview={onTrimClipPreview}
              onSnapPositionChange={setSnapPosition}
              onNormalizeTimelineOrigin={onNormalizeTimelineOrigin}
              playheadPixel={playheadPixel}
              height={VIDEO_TRACK_HEIGHT}
              videoTrackCount={videoTrackCount}
              locked={readOnly}
              minWidth={MIN_TIMELINE_WIDTH}
              onDropReference={onDropReference}
            />

            {/* Audio tracks */}
            <TimelineTrack
              type="audio"
              label="Audio"
              clips={timelineState.audioClips}
              zoomLevel={timelineState.zoomLevel}
              totalDuration={timelineState.totalDuration}
              selectedClipIds={timelineState.selectedClipIds}
              onSelectClip={(id) => onSelectClip(id, 'audio')}
              onToggleClipSelection={onToggleClipSelection}
              onMultiDragOffset={onMultiDragOffset}
              onTrimClip={onTrimClip}
              onAudioTrimClip={onAudioTrimClip}
              onAudioTrimClipPreview={onAudioTrimClipPreview}
              onAudioOffsetChange={onAudioOffsetChange}
              onAudioTrackIndexChange={onAudioTrackIndexChange}
              audioTrackCount={audioTrackCount}
              height={AUDIO_TRACK_HEIGHT}
              locked={readOnly}
              minWidth={MIN_TIMELINE_WIDTH}
              onDropReference={onDropReference}
            />

            {/* Playhead (overlays all tracks) */}
            <TimelinePlayhead
              position={timelineState.playheadPosition}
              positionRef={playheadPositionRef}
              totalDuration={timelineState.totalDuration}
              zoomLevel={timelineState.zoomLevel}
              height={totalHeight}
              onPositionChange={onSetPlayhead}
              isPlaying={timelineState.isPlaying}
            />

            {/* Snap indicator (yellow line during drag) */}
            {snapPosition !== null && (
              <div
                className="absolute top-0 w-0.5 bg-yellow-400 pointer-events-none z-30 animate-pulse"
                style={{
                  left: `${snapPosition}px`,
                  height: `${totalHeight}px`,
                  boxShadow: '0 0 8px rgba(250, 204, 21, 0.6)',
                }}
              />
            )}
          </div>
        </div>
      </div>

      {!readOnly && onAddTrack && (
        <div className="relative flex items-center justify-center border-t border-border-subtle bg-card-hover px-4 py-1.5">
          <button
            type="button"
            className="h-7 w-7 rounded-lg bg-surface-1 border border-input-border hover:bg-surface-3 hover:border-input-border-hover text-foreground flex items-center justify-center transition-colors"
            onClick={() => setIsTrackMenuOpen((open) => !open)}
            data-testid="timeline-add-track-button"
            title="Add track"
          >
            <PlusIcon />
          </button>
          {isTrackMenuOpen && (
            <div className="absolute bottom-10 rounded-lg border border-border bg-background shadow-xl p-1 flex gap-1">
              <button
                type="button"
                className="h-7 rounded-lg px-2 text-xs text-foreground hover:bg-surface-2"
                onClick={() => {
                  onAddTrack('video')
                  setIsTrackMenuOpen(false)
                }}
                data-testid="timeline-add-video-track"
              >
                Video Track
              </button>
              <button
                type="button"
                className="h-7 rounded-lg px-2 text-xs text-foreground hover:bg-surface-2"
                onClick={() => {
                  onAddTrack('audio')
                  setIsTrackMenuOpen(false)
                }}
                data-testid="timeline-add-audio-track"
              >
                Audio Track
              </button>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {timelineState.clips.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <div className="text-text-muted text-lg mb-2">No clips on timeline</div>
            <div className="text-foreground/30 text-sm">
              Connect video nodes to the VideoEditor node to add clips
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ---- Toolbar button ---- */

interface ToolbarButtonProps {
  label: string
  icon: React.ReactNode
  onClick?: () => void
  disabled?: boolean
  title?: string
  testid?: string
}

function ToolbarButton({ label, icon, onClick, disabled, title, testid }: ToolbarButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={`
        flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors
        ${disabled
          ? 'text-text-muted cursor-not-allowed'
          : 'text-text-secondary hover:bg-canvas hover:text-foreground'
        }
      `}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  )
}

/* ---- Toolbar toggle (RFC-0651 #3) ---- */

interface ToolbarToggleProps extends ToolbarButtonProps {
  pressed: boolean
}

function ToolbarToggle({
  label,
  icon,
  onClick,
  disabled,
  title,
  testid,
  pressed,
}: ToolbarToggleProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      aria-pressed={pressed}
      className={`
        flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors
        ${disabled
          ? 'text-text-muted cursor-not-allowed'
          : pressed
            ? 'bg-primary/15 text-primary'
            : 'text-text-secondary hover:bg-canvas hover:text-foreground'
        }
      `}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  )
}

/* ---- Icons (inline SVG) ---- */

function SplitIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m-4-4l4 4 4-4" />
    </svg>
  )
}

function DeleteIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  )
}

function CloseAllGapsIcon() {
  // Collapse arrows: two vertical bars with arrows pointing inward
  // |→  ←|  — universal "close all gaps" / "collapse" symbol
  return (
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      data-testid="timeline-close-all-gaps-icon"
    >
      {/* Left bar */}
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5v14" />
      {/* Right bar */}
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 5v14" />
      {/* Left arrow pointing right */}
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 12h6m0 0l-2.5-2.5M12 12l-2.5 2.5" />
      {/* Right arrow pointing left */}
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12h-6m0 0l2.5-2.5M12 12l2.5 2.5" />
    </svg>
  )
}

function CopyIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  )
}

function PasteIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  )
}

function UndoIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 015 5v2M3 10l4 4M3 10l4-4" />
    </svg>
  )
}

function RedoIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a5 5 0 00-5 5v2M21 10l-4 4M21 10l-4-4" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14m7-7H5" />
    </svg>
  )
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}
